/**
 * Hooks React Query pour les leçons V2.
 *
 * Lecture : edge fn `lesson-get-for-edit` (payload normalisé).
 * Écriture : edge fn `lesson-blocks-mutate` (batch d'ops atomiques).
 * Liens : edge fn `lesson-block-link-mutate` (link / unlink vers un exercice).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type { LessonBlockLinkOp, LessonBlockMutationOp, LessonV2Payload } from './types';

export const lessonV2QueryKey = (lessonId: string) => ['lesson-v2', lessonId] as const;

export function useLessonV2(lessonId: string | null | undefined) {
  return useQuery({
    queryKey: lessonV2QueryKey(lessonId ?? ''),
    enabled: !!lessonId,
    staleTime: 30 * 1000,
    queryFn: async (): Promise<LessonV2Payload> => {
      const { data, error } = await supabase.functions.invoke<LessonV2Payload>(
        'lesson-get-for-edit',
        { body: { lesson_id: lessonId } },
      );
      if (error) throw error;
      if (!data) throw new Error('empty_response');
      return data;
    },
  });
}

export interface LessonBlocksMutateResponse {
  ok: boolean;
  applied: Array<{ op: string; id?: string; client_id?: string }>;
  client_id_map: Record<string, string>;
}

/** Extrait le body utile (reason) d'une FunctionsHttpError avant de jeter. */
async function throwWithContext(error: unknown): Promise<never> {
  const ctx = (error as Error & { context?: Response }).context;
  if (ctx && typeof ctx.text === 'function') {
    try {
      const text = await ctx.text();
      throw new Error(`${(error as Error).message} — ${text}`);
    } catch (parseErr) {
      if (parseErr instanceof Error && parseErr.message.includes('—')) throw parseErr;
      throw error as Error;
    }
  }
  throw error as Error;
}

export function useLessonBlocksMutate(lessonId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (ops: LessonBlockMutationOp[]): Promise<LessonBlocksMutateResponse> => {
      const { data, error } = await supabase.functions.invoke<LessonBlocksMutateResponse>(
        'lesson-blocks-mutate',
        { body: { lesson_id: lessonId, ops } },
      );
      if (error) await throwWithContext(error);
      if (!data) throw new Error('empty_response');
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: lessonV2QueryKey(lessonId) });
      qc.invalidateQueries({ queryKey: ['admin-lesson-detail', lessonId] });
    },
  });
}

export function useLessonBlockLinkMutate(lessonId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (op: LessonBlockLinkOp): Promise<{ ok: boolean; link_id?: string }> => {
      const { data, error } = await supabase.functions.invoke('lesson-block-link-mutate', {
        body: op,
      });
      if (error) await throwWithContext(error);
      return data as { ok: boolean; link_id?: string };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: lessonV2QueryKey(lessonId) });
    },
  });
}

export interface V2ExerciseOption {
  id: string;
  title: string;
}

/**
 * Liste les exercices V2 d'un chapitre, pour alimenter le sélecteur de liaison
 * (LinkPicker). On se restreint au chapitre de la leçon : c'est le périmètre
 * pédagogiquement pertinent et ça borne la taille de la liste.
 */
export function useChapterV2Exercises(chapterId: string | null | undefined) {
  return useQuery({
    queryKey: ['chapter-v2-exercises', chapterId ?? ''],
    enabled: !!chapterId,
    staleTime: 60 * 1000,
    queryFn: async (): Promise<V2ExerciseOption[]> => {
      const { data, error } = await supabase
        .from('exercises')
        .select('id, title')
        .eq('chapter_id', chapterId as string)
        .eq('content_version', 'v2')
        .order('created_at', { ascending: true });
      if (error) throw new Error(error.message);
      return (data ?? []) as V2ExerciseOption[];
    },
  });
}

/**
 * Helper : génère un client_id stable et unique pour une nouvelle op insert.
 * Utilisé pour résoudre les parent_id transitifs dans un batch.
 */
let clientIdCounter = 0;
export function nextClientId(prefix = 'tmp'): string {
  clientIdCounter += 1;
  return `${prefix}_${Date.now()}_${clientIdCounter}`;
}
